import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { isFuture } from 'date-fns';
import type { ComponentProps, ReactNode } from 'react';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';

import { SellCommonCard } from 'components/commonCard/SellCommonCard';
import type { MyCardsSelectionMode } from 'lib/my-cards';

import { TokenBoostDetails } from '../TokenBoostDetails';
import { TokenDetails } from '../TokenDetails';
import { TokenProperties } from '../TokenProperties';
import { TokenRoot } from '../TokenRoot';
import { TokenSealingFooter } from '../TokenSealingFooter';
import type { TokenContent_anyCard } from './__generated__/index.graphql';

type Props = {
  card: TokenContent_anyCard;
  hideOwner?: boolean;
  disableSportSpecific?: boolean;
  galleryOwnerSlug?: string;
  hideSorareUser?: boolean;
  stackedTokensCount?: number;
  onInfoClick?: () => void;
  hideDetails: boolean;
  action?: ReactNode;
  selectionMode?: MyCardsSelectionMode;
  onToggleCard?: (
    slug: string,
    options?: { xpNeeded?: number; sealableFor?: number }
  ) => void;
  selected?: boolean;
  forceShowActionButton?: boolean;
  isDoublonOfSelectedCardSlug?: boolean;
} & Pick<ComponentProps<typeof TokenBoostDetails>, 'totalXpNeeded'>;

export const TokenContent = ({
  card,
  stackedTokensCount,
  disableSportSpecific,
  hideOwner,
  hideSorareUser,
  galleryOwnerSlug,
  onInfoClick,
  hideDetails,
  action,
  selectionMode,
  onToggleCard,
  selected,
  totalXpNeeded,
  forceShowActionButton,
  isDoublonOfSelectedCardSlug,
}: Props) => {
  const { sport } = useSportContext();
  const { currentUser } = useCurrentUserContext();

  const isDesktopLayout = true;

  const isBundledAuction = !!(
    card.latestEnglishAuction &&
    isFuture(card.latestEnglishAuction.endDate) &&
    card.latestEnglishAuction?.cards?.length > 1
  );

  const showLoyaltyBoost =
    card.sealed &&
    currentUser?.slug === galleryOwnerSlug &&
    sport === Sport.BASEBALL;

  return (
    <TokenRoot
      anyCard={card}
      hideDetails={hideDetails}
      stackedTokensCount={stackedTokensCount}
      onToggleCard={onToggleCard}
      selected={selected}
      action={action}
      selectionMode={selectionMode}
      totalXpNeeded={totalXpNeeded}
      galleryOwnerSlug={galleryOwnerSlug}
      forceShowActionButton={forceShowActionButton}
      isDoublonOfSelectedCardSlug={isDoublonOfSelectedCardSlug}
    >
      {!showLoyaltyBoost && (
        <>
          {(!selectionMode ||
            selectionMode === 'drop' ||
            selectionMode === 'sealing') && (
            <TokenProperties
              card={card}
              isBundledAuction={isBundledAuction}
              disableSportSpecific={disableSportSpecific}
              onInfoClick={onInfoClick}
            />
          )}
          {!selectionMode && card.rarity !== Rarity.common && (
            <TokenDetails
              card={card}
              stackedTokensCount={stackedTokensCount}
              isDesktopLayout={isDesktopLayout}
              hideSorareUser={hideSorareUser}
              hideOwner={hideOwner}
              galleryOwnerSlug={galleryOwnerSlug}
              disableSportSpecific={disableSportSpecific}
            />
          )}
          {selectionMode === 'drop' && card.rarity === Rarity.common && (
            <SellCommonCard
              card={card}
              onSelect={() => onToggleCard?.(card.slug)}
              selected={selected}
            />
          )}
          {selectionMode === 'boost' && (
            <TokenBoostDetails
              anyCard={card}
              onInfoClick={onInfoClick}
              totalXpNeeded={totalXpNeeded}
              onToggleCard={onToggleCard}
              selected={selected}
            />
          )}
        </>
      )}
      {!!(showLoyaltyBoost || selectionMode === 'sealing') && (
        <TokenSealingFooter
          anyCard={card}
          selected={selected}
          onSelect={() =>
            onToggleCard?.(card.slug, {
              sealableFor: card.sealableFor || 0,
            })
          }
        />
      )}
    </TokenRoot>
  );
};

TokenContent.fragments = {
  anyCard: gql`
    fragment TokenContent_anyCard on AnyCardInterface {
      slug
      sealed
      sealableFor
      latestEnglishAuction {
        id
        cards: anyCards {
          slug
        }
      }
      ...TokenRoot_anyCard
      ...TokenProperties_anyCard
      ...TokenDetails_anyCard
      ...SellCommonCard_anyCard
      ...TokenBoostDetails_anyCard
      ...TokenSealingFooter_anyCard
    }
    ${TokenRoot.fragments.anyCard}
    ${TokenProperties.fragments.anyCard}
    ${TokenDetails.fragments.anyCard}
    ${SellCommonCard.fragments.anyCard}
    ${TokenBoostDetails.fragments.anyCard}
    ${TokenSealingFooter.fragments.anyCard}
  ` as TypedDocumentNode<TokenContent_anyCard>,
};
